import { e2eeCache } from 'APP/cache/e2ee';
import { EpochRecord, GroupStateStorage } from 'APP/libs/mls-rs-wasm';

export class MlsGroupStateStorageMemory implements GroupStateStorage {
  private groupStates = new Map<string, ArrayBuffer>();
  private epochStates = new Map<string, Map<bigint, ArrayBuffer>>();
  private groupStatesMemory = new Map<string, ArrayBuffer>();
  private groupMaxEpoch = new Map<string, bigint>();
  private clientId: string;

  constructor(clientId: string) {
    this.clientId = clientId;
  }

  async init(): Promise<void> {
    await this.getFromStorage();
  }

  deleteGroupFromStorage(id: string): void {
    this.groupStates.delete(id);
    this.epochStates.delete(id);
    this.groupMaxEpoch.delete(id);
    this.groupStatesMemory.forEach((value, key) => {
      if (key.includes(id)) {
        this.groupStatesMemory.delete(key);
      }
    });
    this.setToStorage();
  }

  private async getGroupStatesFromStorage(): Promise<void> {
    const storageGroupStates = await e2eeCache.getGroupState(this.clientId);

    if (storageGroupStates) {
      this.groupStates = storageGroupStates;
    }
  }

  private async getGroupStatesMemoryFromStorage(): Promise<void> {
    const groupStatesMemoryStorage = await e2eeCache.getGroupStatesMemory(this.clientId);

    if (groupStatesMemoryStorage) {
      this.groupStatesMemory = groupStatesMemoryStorage;
    }
  }

  private async getEpochStatesFromStorage(): Promise<void> {
    const epochStatesStorage = await e2eeCache.getEpochState(this.clientId);

    if (epochStatesStorage) {
      this.epochStates = epochStatesStorage;
    }
  }

  private async getGroupMaxEpochFromStorage(): Promise<void> {
    const groupMaxEpochStorage = await e2eeCache.getGroupMaxEpoch(this.clientId);

    if (groupMaxEpochStorage) {
      this.groupMaxEpoch = groupMaxEpochStorage;
    }
  }

  private async getFromStorage(): Promise<void> {
    await this.getGroupStatesFromStorage();
    await this.getEpochStatesFromStorage();
    await this.getGroupMaxEpochFromStorage();
    await this.getGroupStatesMemoryFromStorage();
  }

  private setToStorage(): void {
    e2eeCache.putGroupState(this.clientId, this.groupStates);
    e2eeCache.putGroupStatesMemory(this.clientId, this.groupStatesMemory);
    e2eeCache.putEpochState(this.clientId, this.epochStates);
    e2eeCache.putGroupMaxEpoch(this.clientId, this.groupMaxEpoch);
  }

  clearStorage(): void {
    e2eeCache.putGroupState(this.clientId, new Map<string, ArrayBuffer>());
    e2eeCache.putGroupStatesMemory(this.clientId, new Map<string, ArrayBuffer>());
    e2eeCache.putEpochState(this.clientId, new Map<string, Map<bigint, ArrayBuffer>>());
    e2eeCache.putGroupMaxEpoch(this.clientId, new Map<string, bigint>());
  }

  state(groupId: ArrayBuffer): ArrayBuffer | undefined {
    const groupIdString = new TextDecoder().decode(groupId);
    const groupState = this.groupStatesMemory.get(groupIdString);

    return groupState || this.groupStates.get(groupIdString);
  }

  epoch(groupId: ArrayBuffer, epochId: bigint): ArrayBuffer | undefined {
    const groupIdString = new TextDecoder().decode(groupId);
    const epochState = this.epochStates.get(groupIdString);
    return epochState?.get(epochId);
  }

  write(
    groupId: ArrayBuffer,
    groupState: ArrayBuffer,
    epochInserts: Array<EpochRecord>,
    epochUpdates: Array<EpochRecord>
  ): void {
    const groupIdString = new TextDecoder().decode(groupId);
    this.groupStates.set(groupIdString, groupState);

    let epochs = this.epochStates.get(groupIdString);
    if (!epochs) {
      epochs = new Map<bigint, ArrayBuffer>();
    }
    const records = [...epochInserts, ...epochUpdates];

    records.forEach((record) => {
      epochs?.set(record.id, record.data);
      const currentMax = this.groupMaxEpoch.get(groupIdString) || record.id;

      this.groupMaxEpoch.set(
        groupIdString,
        Number(currentMax) > Number(record.id) ? currentMax : record.id
      );
    });

    if (groupState && epochInserts.length) {
      epochInserts.forEach((record) => {
        this.groupStatesMemory.set(`${groupIdString}:${Number(record.id)}`, groupState);
      });
    }
    this.epochStates.set(groupIdString, epochs);
    this.setToStorage();
  }

  maxEpochId(groupId: ArrayBuffer): bigint | undefined {
    const groupIdString = new TextDecoder().decode(groupId);
    return this.groupMaxEpoch.get(groupIdString);
  }
}
